import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress, Chip } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import ImageIcon from '@mui/icons-material/Image';
import { adminApi } from '../../api';

const StatCard = ({ label, value, sub, icon, color = 'primary.main' }) => (
  <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2.5, borderRadius: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{ color: '#555', fontSize: 11, letterSpacing: 1, fontFamily: 'monospace' }}>
          {label.toUpperCase()}
        </Typography>
        <Typography sx={{ color, fontSize: 32, fontWeight: 700, fontFamily: 'monospace', lineHeight: 1.2, mt: 0.5 }}>
          {value ?? '—'}
        </Typography>
        {sub && <Typography sx={{ color: '#555', fontSize: 11, mt: 0.5 }}>{sub}</Typography>}
      </Box>
      <Box sx={{ color: '#222', fontSize: 40 }}>{icon}</Box>
    </Box>
  </Paper>
);

const TierBar = ({ hot = 0, cool = 0, archive = 0 }) => {
  const total = hot + cool + archive || 1;
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: '#1a1a1a' }}>
        <Box sx={{ width: `${hot / total * 100}%`, bgcolor: '#00bcd4' }} />
        <Box sx={{ width: `${cool / total * 100}%`, bgcolor: '#7986cb' }} />
        <Box sx={{ width: `${archive / total * 100}%`, bgcolor: '#ff8a65' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.8 }}>
        {[['Hot', '#00bcd4', hot], ['Cool', '#7986cb', cool], ['Archive', '#ff8a65', archive]].map(([l, c, v]) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, bgcolor: c, borderRadius: '50%' }} />
            <Typography sx={{ color: '#666', fontSize: 11 }}>{l}: {v}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Overview = () => {
  const [overview, setOverview] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminApi.getOverview(), adminApi.getFacilityStats()])
      .then(([ov, fac]) => { setOverview(ov); setFacilities(fac); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const fmtBytes = (b) => {
    if (!b) return '0 GB';
    const gb = b / 1e9;
    return gb > 1000 ? `${(gb / 1000).toFixed(1)} TB` : `${gb.toFixed(1)} GB`;
  };

  return (
    <Box>
      <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1, mb: 3 }}>
        System Overview
      </Typography>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Total Studies" value={overview?.total_studies?.toLocaleString()} icon={<FolderIcon fontSize="inherit" />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Patients" value={overview?.total_patients?.toLocaleString()} icon={<PeopleIcon fontSize="inherit" />} color="#66bb6a" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Facilities" value={overview?.total_facilities} icon={<BusinessIcon fontSize="inherit" />} color="#ce93d8" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Total Instances" value={overview?.total_instances?.toLocaleString()} sub={fmtBytes(overview?.total_bytes)} icon={<ImageIcon fontSize="inherit" />} color="#ffb74d" />
        </Grid>
      </Grid>

      {/* Storage tier */}
      <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2.5, borderRadius: 1, mb: 2 }}>
        <Typography sx={{ color: '#aaa', fontSize: 13, fontWeight: 600, mb: 1 }}>Storage Tiers</Typography>
        <TierBar hot={overview?.hot_count} cool={overview?.cool_count} archive={overview?.archive_count} />
        {overview?.pending_delete_count > 0 && (
          <Chip label={`${overview.pending_delete_count} studies pending deletion`} size="small"
            sx={{ mt: 1, bgcolor: '#3e1a1a', color: '#ef9a9a', fontSize: 11 }} />
        )}
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: '#555', fontSize: 11 }}>Active LCM rules: {overview?.active_lcm_rules}</Typography>
        </Box>
      </Paper>

      {/* Per-facility breakdown */}
      <Paper sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', p: 2.5, borderRadius: 1 }}>
        <Typography sx={{ color: '#aaa', fontSize: 13, fontWeight: 600, mb: 2 }}>Facilities</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {facilities.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ color: '#e0e0e0', fontSize: 13, minWidth: 160, fontFamily: 'monospace' }}>
                {f.facility_name}
              </Typography>
              <Typography sx={{ color: '#555', fontSize: 12, minWidth: 80 }}>
                {f.study_count?.toLocaleString()} studies
              </Typography>
              <Typography sx={{ color: '#555', fontSize: 12, minWidth: 80 }}>
                {f.patient_count?.toLocaleString()} patients
              </Typography>
              <Typography sx={{ color: '#555', fontSize: 12, minWidth: 100 }}>
                {f.total_instances?.toLocaleString()} instances
              </Typography>
              <Box sx={{ flex: 1 }}>
                <TierBar hot={f.hot_count} cool={f.cool_count} archive={f.archive_count} />
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default Overview;
